import { ReactNode, useMemo, useCallback, memo } from 'react';
import { Progress as FlowBiteProgressBar } from 'flowbite-react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { useSetAtom, useAtomValue } from 'jotai';

import {
  cn,
  computeRange,
  getAssetSrc,
  openInNewTab,
  PersonWithRestworldCupIllustration,
  CVParsingLoadingIllustrationInitial,
  useAnalytics,
} from '@restworld/utils-common';
import { Heading, Button } from '@restworld/ui-ds-base';

import { useStepData } from '../worker.cvloading-ui.constant';
import { CheckMarkIcon } from './svg';
import { entryPointModalVisibleAtom } from '../../worker-entry-point-modal';
import { workerManagedFooterMapKeyAtom } from '../../../worker-managed-using-klaaryo';
import { isCVUploadProcessCompletedAtom } from '../../worker-entry-point.atoms';

const numberList = [1, 2, 3, 4];
export const WorkerCVLoadingUIWrapper = ({
  children,
  activeIndex,
  isCompleted,
  testId,
}: {
  children: ReactNode;
  activeIndex: number;
  isCompleted: boolean;
  // Kept for backward-compat with the parent; the post-CV redirect no longer
  // needs it (see handleClick below).
  workerId?: string;
  testId?: string;
}) => {
  const progressTabElList = useMemo(
    () =>
      numberList.map((v, i) => {
        const activeIndexes = [0, ...computeRange(activeIndex + 1)];
        return (
          <ProgressTabWrapper key={v} isActiveIndex={activeIndexes.includes(i)} isProceedingBar={activeIndex + 1 === i}>
            {activeIndexes.includes(i) && (
              <ProgressTab>
                <CheckMarkIcon {...{ height: 24, width: 24 }} />
              </ProgressTab>
            )}
          </ProgressTabWrapper>
        );
      }),
    [activeIndex]
  );

  return (
    <MainWrapper data-cy={testId}>
      <SubWrapper>{progressTabElList}</SubWrapper>
      <ChildrenWrapper>{children}</ChildrenWrapper>
      {isCompleted ? (
        <div
          data-cy="success-illustration"
          className="[animation: scale-up-center 1s cubic-bezier(0.390, 0.575, 0.565, 1.000) both]"
        >
          <PersonWithRestworldCupIllustration {...{ width: 300, height: 300 }} />
        </div>
      ) : (
        <>
          <div className="animate-pulse">
            <CVParsingLoadingIllustrationInitial {...{ width: 300, height: 300 }} />
          </div>
          <ProgressBarBlock activeIndex={activeIndex} />
        </>
      )}
    </MainWrapper>
  );
};

/**
 * Primary CTA for the CV-loading / CV-completed screen. Rendered in the modal's fixed `footer`
 * slot (not inside the scrollable body) so it stays pinned at the bottom while the content scrolls.
 * Reads `isCVUploadProcessCompletedAtom` directly since it lives outside the body's render tree.
 */
export const WorkerCVLoadingFooterCTA = () => {
  const { t } = useTranslation();
  const title = useMemo(() => t('onboarding-page:check_your_profile'), [t]);
  const { trackEvent } = useAnalytics();

  const isCompleted = useAtomValue(isCVUploadProcessCompletedAtom);
  const toggleEntryPoint = useSetAtom(entryPointModalVisibleAtom);
  const setWorkerManagedFooterMapKey = useSetAtom(workerManagedFooterMapKeyAtom);

  const handleClick = useCallback(() => {
    if (isCompleted) {
      trackEvent('check_data_cv', { close_button: true });
      // After CV creation the user is already auto-logged-in, so send them
      // straight to their profile. We must never route to /signup (that page
      // only renders the lead-minting signup form, and the AuthGuard would
      // bounce an authenticated worker to /profilo anyway).
      openInNewTab('/profilo');
      setWorkerManagedFooterMapKey('cv_applied');
      toggleEntryPoint(false);
    }
  }, [isCompleted, setWorkerManagedFooterMapKey, toggleEntryPoint, trackEvent]);

  return (
    <Button variant="primary" disabled={!isCompleted} onClick={handleClick} fullwidth>
      {title}
    </Button>
  );
};
export const Description = memo(
  ({
    title: mainTitle,
    subTitle,
    additionalContent,
  }: {
    title: string;
    subTitle: string;
    additionalContent?: string;
  }) => {
    return (
      <DescriptionWrapper>
        <Heading testId="cv-upload-complete-title" align="center">
          {mainTitle}
        </Heading>
        <Heading align="center" weight="normal" color="muted">
          {subTitle}
        </Heading>
        {!!additionalContent && (
          <Heading align="center" weight="normal" color="muted">
            {additionalContent}
          </Heading>
        )}
      </DescriptionWrapper>
    );
  }
);
export const ProgressBarBlock = memo(({ activeIndex }: { activeIndex: number }) => {
  const stepData = useStepData();
  return (
    <ProgressBarMainWrapper>
      <ProgressBarAvatar>
        <Image height={32} width={32} src={getAssetSrc(stepData[activeIndex].progressLabel.icon)} alt="" />
      </ProgressBarAvatar>
      <ProgressBarTitleBarWrapper>
        <Heading size={5} weight="bold">
          {stepData[activeIndex].progressLabel.title}
        </Heading>
        <ProgressStyled progress={stepData[activeIndex].progressPercent} color="purple" />
        <ProgressBarTitleBar>
          <Heading size={6} weight="medium" color="muted">
            {stepData[activeIndex].progressLabel.remainingTime}
          </Heading>
          <Heading size={6} weight="medium" color="muted">{`${stepData[activeIndex].progressPercent}%`}</Heading>
        </ProgressBarTitleBar>
      </ProgressBarTitleBarWrapper>
    </ProgressBarMainWrapper>
  );
});
export const ProgressStyled = ({ className, ...props }: React.ComponentProps<typeof FlowBiteProgressBar>) => (
  <FlowBiteProgressBar
    // flowbite applies theme.size[size] (md = h-2.5) to BOTH the track and the fill, and it beats
    // a plain `h-1` className -> a 4px fill floating inside a 10px track. Override the size so both
    // are h-1 (master's thin bar).
    theme={{ size: { md: 'h-1' } }}
    className={cn('[&>div:first-child]:bg-primary-800 [&>div:first-child]:shadow', className)}
    {...props}
  />
);
const ChildrenWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col gap-4 w-full', className)} {...props} />
);
const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col gap-6 items-center w-full animate-[scale-up-center_0.4s_cubic-bezier(0.390,0.575,0.565,1.000)_both]',
      className
    )}
    {...props}
  />
);

const SubWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex gap-1 pt-4 w-full justify-center', className)} {...props} />
);
const ProgressTabWrapper = ({
  isActiveIndex,
  isProceedingBar,
  className,
  ...props
}: {
  isActiveIndex: boolean;
  isProceedingBar: boolean;
} & React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'relative h-1.5 w-24 z-0 rounded-sm flex justify-center',
      isActiveIndex ? 'bg-primary-800' : isProceedingBar ? 'bg-primary-300' : 'bg-gray-200',
      className
    )}
    {...props}
  />
);
const ProgressTab = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('absolute mx-auto flex -top-2 z-10', className)} {...props} />
);
const DescriptionWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('text-center flex flex-col gap-4', className)} {...props} />
);
const ProgressBarMainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn('rounded-lg flex gap-4 bg-primary-50 w-full p-2 justify-start items-center', className)}
    {...props}
  />
);
const ProgressBarAvatar = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-lg bg-white p-3 flex items-center justify-center', className)} {...props} />
);
const ProgressBarTitleBarWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex flex-col w-full gap-2 rounded', className)} {...props} />
);
const ProgressBarTitleBar = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex justify-between', className)} {...props} />
);
